/**
 * The shift report's STATE - the half of ui/ShiftReport.ts that was never
 * about rendering, moved out so the React pane can be a pure projection.
 *
 * A student arrives at a tool, is handed a complaint with no diagnosis
 * attached, and has to commit to a call before anything is revealed; the
 * readings cost hours and a wrong call is the repair twice. All of that
 * pedagogy lives here; the pane (ui/kit/ShiftReportPane.tsx) just shows it.
 *
 * Session-long, like the panel store that owns it: the armed fault, the paid
 * readings and the committed call all survive close and reopen. The house
 * and mission subscriptions are made once at construction - one per tool for
 * the life of the session, exactly what the old class did.
 */

import { missionState } from '../learn/missionState'
import type { Journal } from '../learn/Journal'
import type { HouseBus } from '../sim/house/model'
import { repairLabel } from '../ui/Debrief'
import {
  diagnose,
  randomFault,
  readingCostHours,
  type ConditionModel,
  type ToolFault,
} from '../sim/faults/model'
import { localiseFault, toolLabelOf } from '../sim/faults/localise'
import { PanelState } from './PanelState'

export interface ShiftStateOptions<A extends string, C> {
  journal: Journal
  /** The shared house - when supplied the tool follows fab-wide events. */
  house?: HouseBus
  /** Which of this tool's scenarios each house event corresponds to. */
  houseScenarios?: Record<string, string>
  /** Push the condition into the owner's sim whenever it changes. */
  onCondition?: (condition: C, fault: ToolFault<A, C>) => void
}

export class ShiftState<A extends string, C> extends PanelState {
  fault: ToolFault<A, C>
  condition: C
  /** Ids of the earned readings the student has paid for. */
  readonly taken = new Set<string>()
  call: { aspect: A; correct: boolean } | null = null
  /** Instructor view: shows the answer key without committing. */
  revealed = false

  private readonly healthyId: string

  constructor(
    readonly model: ConditionModel<A, C>,
    private readonly opts: ShiftStateOptions<A, C>,
  ) {
    super()
    this.healthyId = (model.faults.find((f) => f.aspect === null) ?? model.faults[0]).id
    this.fault = model.faults.find((f) => f.aspect === null) ?? model.faults[0]
    this.condition = this.fault.apply(model.nominal)

    // Follow the building. A tool that is not plumbed into the armed system
    // returns to a healthy shift rather than holding whatever it had.
    opts.house?.subscribe((id) => {
      const mapped = id === null ? this.healthyId : opts.houseScenarios?.[id]
      this.startShift(mapped ?? this.healthyId)
    })
    // And catch up with it. A subscription only hears what changes AFTER it
    // is made, and this store is made the first time its panel opens - which,
    // on a building order, is after the order armed the house. The etcher
    // opened onto a warm cooling-water loop with a healthy shift, and the
    // student's correct call ("escalate to facilities") was scored wrong
    // against a truth of "nothing". Same handshake the work order gets below.
    const armed = opts.house?.armed
    if (armed) {
      const mapped = opts.houseScenarios?.[armed]
      if (mapped && mapped !== this.fault.id) this.startShift(mapped)
    }

    // Follow the work order - the handshake that lets a mission arm a shift
    // on a panel the student has not opened yet.
    const applyOrder = (): void => {
      const wanted = missionState.pendingFaultFor(model.toolId)
      if (wanted && wanted !== this.fault.id) this.startShift(wanted)
    }
    missionState.subscribe(applyOrder)
    applyOrder()
  }

  /** This tool's own name, translated - a getter so language switches follow. */
  get toolName(): string {
    return toolLabelOf(this.model)
  }

  /** The condition the tool is actually in, for an owner that wants to run it. */
  get toolCondition(): C {
    return this.condition
  }

  /** Hand the student a new shift. Without an id, one is drawn at random. */
  startShift(id?: string): void {
    const picked = id ? this.model.faults.find((f) => f.id === id) : randomFault(this.model)
    this.fault = picked ?? this.fault
    this.condition = this.fault.apply(this.model.nominal)
    this.taken.clear()
    this.call = null
    this.revealed = false
    this.opts.journal.record({
      kind: 'shiftStart',
      fault: this.fault.id,
      complaint: this.fault.complaint,
      tool: this.toolName,
      toolId: this.model.toolId,
    })
    this.opts.onCondition?.(this.condition, this.fault)
    this.touch()
  }

  /** Hours of tool time this shift has spent on readings. */
  hoursOnReadings(): number {
    let h = 0
    for (const r of this.model.earnedReadings(this.condition)) {
      if (this.taken.has(r.id)) h += readingCostHours(r.label)
    }
    return h
  }

  takeReading(r: { id: string; label: string }): void {
    this.taken.add(r.id)
    this.opts.journal.record({
      kind: 'reading',
      what: r.label,
      hours: readingCostHours(r.label),
      tool: this.toolName,
      toolId: this.model.toolId,
      readingId: r.id,
    })
    this.touch()
  }

  commit(aspect: A): void {
    const correct = aspect === this.fault.aspect
    const called = this.model.aspects.find((a) => a.id === aspect)
    const truth = this.model.aspects.find((a) => a.id === this.fault.aspect)
    // A wrong call spends the wrong repair AND then the right one.
    const repair = correct
      ? (called?.downtimeHours ?? 0)
      : (called?.downtimeHours ?? 0) + (truth?.downtimeHours ?? 0)

    this.call = { aspect, correct }
    this.opts.journal.record({
      kind: 'call',
      aspect,
      label: called?.label ?? aspect,
      correct,
      truth: this.fault.aspect,
      readingsTaken: this.opts.journal.count('reading'),
      wafersRun: this.opts.journal.count('wafer'),
      questionsAsked: this.opts.journal.count('question'),
      tool: this.toolName,
      toolId: this.model.toolId,
      // Repair only. The earned readings are charged on their own events, so
      // folding them in here would bill the shift for them twice once the
      // debrief adds the events up.
      hoursSpent: repair,
      repairLabel: repairLabel(called?.label ?? aspect, correct ? null : (truth?.label ?? null), correct && aspect === 'house'),
    })
    this.touch()
  }

  toggleReveal(): void {
    this.revealed = !this.revealed
    this.touch()
  }

  /** The diagnosis table for the verdict - runs the physics per aspect. */
  diagnosis() {
    return diagnose(this.model, this.condition)
  }

  /** The armed fault in the student's language. */
  get faultText() {
    return localiseFault(this.model.toolId, this.fault)
  }
}
